import { CommonModule } from '@angular/common'
import { Component, computed, input, output, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { OptimUiSharedModule } from '../../shared/optimus-ui-shared.module'
import { buildVlanDraft, buildVlanTable, serializeVlanFields, validateVlanMetafields, VlanEditableRow, VlanFieldCheck, VlanRow, VlanRowStatus } from '../../_helper/vlan-format.util'

/**
 * Tabella VLAN unificata: vlan_trunk è l'indice, network e descrizione arrivano
 * da tutti i campi vlanlistX / vlandescrX. Riusabile in tutte le sezioni che
 * mostrano i metafield VLAN (dialog "Check VLANs", site-hub, dashboard).
 */
@Component({
  selector: 'app-vlan-table',
  standalone: true,
  imports: [CommonModule, FormsModule, OptimUiSharedModule],
  templateUrl: './vlan-table.component.html'
})
export class VlanTableComponent {
  readonly fields = input<Record<string, any> | null | undefined>(null)
  /** Codice/frazionario mostrato accanto al titolo. */
  readonly code = input<string | null>(null)
  /** Mostra la riga di titolo "VLAN <code>". */
  readonly showTitle = input(true)
  /** Mostra il disclosure con i valori grezzi dei campi. */
  readonly showRaw = input(true)
  /** Abilita la modifica di righe esistenti e l'aggiunta di nuove VLAN. */
  readonly editable = input(false)
  /** Emette il record aggiornato, con i campi VLAN riserializzati nel formato originale. */
  readonly save = output<Record<string, any>>()

  readonly editing = signal(false)
  readonly draft = signal<VlanEditableRow[]>([])
  readonly editErrors = signal<string[]>([])

  readonly table = computed(() => buildVlanTable(this.fields()))

  /** Campi VLAN grezzi, per il disclosure "Valori originali". */
  readonly rawChecks = computed<VlanFieldCheck[]>(() =>
    validateVlanMetafields(this.fields()).filter((check) => check.value !== '')
  )

  readonly statusCounts = computed<Record<VlanRowStatus, number>>(() => {
    const counts: Record<VlanRowStatus, number> = { ok: 0, warning: 0, error: 0 }
    for (const row of this.table().rows) {
      counts[row.status] += 1
    }
    return counts
  })

  readonly alertTone = computed(() =>
    this.table().issues.some((issue) => issue.severity === 'error') ? 'danger' : 'warning'
  )

  statusLabel (status: VlanRowStatus): string {
    switch (status) {
      case 'ok':
        return 'OK'
      case 'warning':
        return 'Parziale'
      default:
        return 'Errore'
    }
  }

  statusClass (status: VlanRowStatus): string {
    switch (status) {
      case 'ok':
        return 'fq-chip-success'
      case 'warning':
        return 'fq-chip-warning'
      default:
        return 'fq-chip-danger'
    }
  }

  issuesText (row: VlanRow): string {
    return row.issues.map((issue) => issue.message).join(' ')
  }

  startEdit (): void {
    this.draft.set(buildVlanDraft(this.fields()))
    this.editErrors.set([])
    this.editing.set(true)
  }

  cancelEdit (): void {
    this.editing.set(false)
    this.editErrors.set([])
  }

  addRow (): void {
    this.draft.update(rows => [...rows, { vlanId: '', network: '', description: '' }])
  }

  removeRow (index: number): void {
    this.draft.update(rows => rows.filter((_, position) => position !== index))
  }

  updateDraft (index: number, key: 'vlanId' | 'network' | 'description', value: string): void {
    this.draft.update(rows => rows.map((row, position) => position === index ? { ...row, [key]: value } : row))
  }

  commitEdit (): void {
    const { fields, errors } = serializeVlanFields(this.fields(), this.draft())
    if (errors.length > 0) {
      this.editErrors.set(errors)
      return
    }
    this.editErrors.set([])
    this.editing.set(false)
    this.save.emit(fields)
  }

  fieldLabel (field: string): string {
    if (field === 'vlan_trunk') {
      return 'VLAN trunk'
    }
    const list = /^vlanlist(\d+)$/.exec(field)
    if (list) {
      return `VLAN list ${list[1]}`
    }
    const descr = /^vlandescr(\d+)$/.exec(field)
    if (descr) {
      return `VLAN descrizione ${descr[1]}`
    }
    return field
  }
}
